import * as React from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import {
  Building2, CalendarDays, KanbanSquare, LogOut, Receipt, Settings, Users
} from 'lucide-react'
import { useTenant } from './TenantProvider.jsx'
import { useAuth } from '@/auth/AuthProvider.jsx'
import { cn, initials } from '@/lib/utils.js'
import { Button } from '@/components/ui/button.jsx'
import { BillingBanner, SubscriptionBlocked } from './Billing.jsx'
import { NotificationBell } from './NotificationBell.jsx'
import { ERR } from '@/lib/errors.js'

/**
 * The application shell (Phase 1b build order step 5).
 *
 * Responsive from day one (D8), and the two layouts are genuinely different
 * rather than one squeezed: a bottom tab bar on phones, because that is where
 * a thumb reaches on a device being held one-handed on a job site, and a side
 * rail on desktop, where vertical space is the scarce resource.
 *
 * Session 1 renders the shell and its navigation against real tenant data.
 * The destinations are session 2's screens; each is a placeholder that says so
 * rather than a dead link, because a nav item that does nothing is worse than
 * one that explains itself.
 */

/**
 * ── THE TWO ZONES (Phase 9 D9.3) ────────────────────────────────────────────
 * CRM FIRST, ALWAYS, and Pipeline first within it. That is not a layout
 * preference: master-roadmap.md's position is that the base tier "isn't a
 * stripped-down product, it IS this loop", and every workspace user sees all
 * of it. Operations is the back office, it appears BELOW CRM, and only for a
 * user holding the grant.
 *
 * `/` stays the Pipeline board and the post-login landing route regardless of
 * Operations access — an office manager with the grant still lands on the
 * board, because that is where the work is.
 */
const CRM_NAV = [
  { to: '/', label: 'Pipeline', icon: KanbanSquare, end: true },
  { to: '/clients', label: 'Clients', icon: Users },
  { to: '/schedule', label: 'Schedule', icon: CalendarDays },
  { to: '/invoices', label: 'Invoices', icon: Receipt },
  { to: '/settings', label: 'Settings', icon: Settings }
]

/**
 * Rendered only for a user with the grant, and OMITTED rather than disabled.
 *
 * D9.3 says so directly: "Unauthorized users never see Operations, not a
 * disabled item." A greyed-out Payroll tab tells a sales hire exactly what
 * they are not trusted with and invites them to ask about it, which is the
 * opposite of what a contractor handing over the CRM wants.
 */
const OPERATIONS_NAV = [
  { to: '/operations', label: 'Operations', icon: Building2 }
]

/** tenants.status → the 402 slug the blocked screen renders for. */
const BLOCKED_STATUS = {
  suspended: ERR.SUBSCRIPTION_SUSPENDED,
  canceled: ERR.SUBSCRIPTION_CANCELED
}

export function AppShell () {
  const { tenant, user, workspaceName, canAccessOperations } = useTenant()
  const { signOut } = useAuth()

  // One list for the phone, because a tab bar has no room for group headings.
  // Six tabs at 375px is the widest this gets, and the grid is sized from the
  // array rather than a hardcoded class so it stays honest when the group is
  // absent (five) or present (six).
  const mobileTabs = canAccessOperations ? [...CRM_NAV, ...OPERATIONS_NAV] : CRM_NAV

  /**
   * A blocked workspace never renders the shell (Phase 0b).
   *
   * Returning early rather than overlaying is deliberate: behind the gate every
   * list is empty and every button 402s, and a contractor cannot distinguish
   * that from "my data is gone". The nav is also removed on purpose — offering
   * five destinations that all fail is worse than offering the one that works.
   *
   * This mirrors requireActiveBilling exactly. It is a courtesy, not the
   * boundary: the API refuses these requests whether or not this branch runs.
   */
  const blockedSlug = BLOCKED_STATUS[tenant.status]
  if (blockedSlug) {
    return (
      <SubscriptionBlocked
        slug={blockedSlug}
        canOpenPortal={Boolean(tenant.stripe_customer_id ?? true)}
        onSignOut={signOut}
      />
    )
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-surface focus:px-4 focus:py-2 focus:shadow-raised"
      >
        Skip to content
      </a>

      {/* ---- desktop rail ------------------------------------------------ */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-line bg-surface lg:flex">
        <div className="flex h-16 items-center gap-3 border-b border-line px-5">
          <WorkspaceMark tenant={tenant} name={workspaceName} />
        </div>

        <nav className="flex-1 space-y-0.5 p-3" aria-label="Main">
          {CRM_NAV.map(item => <RailLink key={item.to} item={item} />)}

          {/* Phase 9 D9.3 — "a visually separate nav group below CRM". The
              rule and the divider are what make it read as a different zone
              rather than four more CRM destinations. */}
          {canAccessOperations && (
            <div className="pt-3">
              <div className="mb-1 border-t border-line pt-3">
                <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                  Operations
                </p>
              </div>
              {OPERATIONS_NAV.map(item => <RailLink key={item.to} item={item} />)}
            </div>
          )}
        </nav>

        <div className="border-t border-line p-3">
          <div className="flex items-center gap-3 rounded-md px-3 py-2">
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-brand-ink"
              aria-hidden="true"
            >
              {initials({ first_name: user.email?.[0] ?? '?' })}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-ink-muted" title={user.email ?? ''}>
              {user.email}
            </span>
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={signOut}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut />
            </Button>
          </div>
        </div>
      </aside>

      {/* ---- mobile header ----------------------------------------------- */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-line bg-surface px-4 lg:hidden">
        <WorkspaceMark tenant={tenant} name={workspaceName} />
        <div className="flex items-center gap-1">
          <NotificationBell />
          <Button variant="ghost" size="icon-sm" onClick={signOut} aria-label="Sign out">
            <LogOut />
          </Button>
        </div>
      </header>

      {/* pb-20 clears the fixed tab bar; without it the last row of every
          list sits permanently under it and looks like a rendering bug. */}
      <main id="main" className="px-4 pb-20 pt-5 sm:px-6 lg:ml-60 lg:pb-10 lg:pt-8">
        <div className="mx-auto w-full max-w-6xl">
          {/* Above the outlet, so it is the first thing read on every screen
              rather than something only the Settings page happens to show. */}
          <BillingBanner status={tenant.status} />
          <Outlet />
        </div>
      </main>

      {/* ---- mobile tab bar ---------------------------------------------- */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 grid border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden"
        // Inline rather than a `grid-cols-N` class: Tailwind compiles the
        // classes it can SEE in the source, so a template-built class name
        // produces no CSS at all and the bar would collapse to one column.
        style={{ gridTemplateColumns: `repeat(${mobileTabs.length}, minmax(0, 1fr))` }}
        aria-label="Main"
      >
        {mobileTabs.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => cn(
              // px-0.5 + break-words keeps the six-tab case from overflowing at
              // 375px, where each tab is about 62px wide.
              'flex min-h-tap flex-col items-center justify-center gap-1 px-0.5 py-2 text-center text-[0.6875rem] font-medium leading-tight transition-colors',
              isActive ? 'text-brand' : 'text-ink-subtle'
            )}
          >
            <item.icon className="size-5 shrink-0" aria-hidden="true" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}

/** One desktop rail destination. Extracted when the rail grew a second group. */
function RailLink ({ item }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-brand-subtle text-brand'
          : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'
      )}
    >
      <item.icon className="size-4 shrink-0" aria-hidden="true" />
      {item.label}
    </NavLink>
  )
}

/**
 * The workspace's logo, or its name.
 *
 * §3.4's rule: a tenant with no logo_url renders text, never a broken image.
 * `onError` covers the other half — a logo_url that 404s must degrade to the
 * same text rather than leaving a torn-image icon in the header, which is the
 * single most visible way a product can look unmaintained.
 */
function WorkspaceMark ({ tenant, name }) {
  const [failed, setFailed] = React.useState(false)
  const logo = tenant?.branding?.logo_url

  if (logo && !failed) {
    return (
      <img
        src={logo}
        alt={name}
        className="h-8 max-w-[10rem] object-contain"
        onError={() => setFailed(true)}
      />
    )
  }

  return (
    <span className="truncate text-base font-semibold tracking-tight text-ink" title={name}>
      {name}
    </span>
  )
}
